import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Package,
  Truck,
  Users,
  Calendar,
  MapPin,
  Clock,

  Apple,
  Coffee,
  Cake,
  Gift,
} from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { archiveSupplyRequest, getMyCreatedSupplyRequests, createSupplyRequest, SupplyRequest } from '../../api/supplyRequests'
import PickupRequestForm, { PickupRequestData } from '../PickupRequestForm'
import { getMyIncomingDonations, markDonationDelivered, Donation } from '../../api/donations'
import { getMyBusinessVerificationStatus } from '../../api/businessVerification'

const FoodBankDashboard = () => {
  const { user } = useUser()
  const [mySupplyRequests, setMySupplyRequests] = useState<SupplyRequest[]>([])
  const [incomingDonations, setIncomingDonations] = useState<Donation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showPickupForm, setShowPickupForm] = useState(false)
  const [isSubmittingPickup, setIsSubmittingPickup] = useState(false)
  const [actionDonationId, setActionDonationId] = useState<string | null>(null)
  const [archivingRequestId, setArchivingRequestId] = useState<string | null>(null)
  const [businessVerification, setBusinessVerification] = useState<any>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myRequests, incoming] = await Promise.all([
          getMyCreatedSupplyRequests(),
          getMyIncomingDonations()
        ])
        setMySupplyRequests(myRequests.slice(0, 5)) // Show first 5
        setIncomingDonations(incoming.slice(0, 5))
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || user.userType !== 'FOOD_BANK') return
    ;(async () => {
      try {
        const s = await getMyBusinessVerificationStatus()
        setBusinessVerification(s)
      } catch {
        setBusinessVerification(null)
      }
    })()
  }, [user?.id, user?.userType])

  const handleConfirmDelivery = async (donationId: string) => {
    try {
      setActionDonationId(donationId)
      await markDonationDelivered(donationId)
      const incoming = await getMyIncomingDonations()
      setIncomingDonations(incoming.slice(0, 5))
    } catch (error) {
      console.error('Error confirming delivery:', error)
      alert('Failed to confirm delivery. Please try again.')
    } finally {
      setActionDonationId(null)
    }
  }

  const handlePickupRequest = async (formData: PickupRequestData) => {
    try {
      setIsSubmittingPickup(true)
      await createSupplyRequest(formData)
      setShowPickupForm(false)
      // Refresh data
      const myRequests = await getMyCreatedSupplyRequests()
      setMySupplyRequests(myRequests.slice(0, 5))
    } catch (error: any) {
      console.error('Error creating supply request:', error)
      alert(error?.message || 'Failed to create supply request. Please try again.')
    } finally {
      setIsSubmittingPickup(false)
    }
  }

  const handleArchiveRequest = async (id: string) => {
    const ok = window.confirm('Take down (archive) this request? You can still view it later in Archived Requests.')
    if (!ok) return
    try {
      setArchivingRequestId(id)
      await archiveSupplyRequest(id)
      const myRequests = await getMyCreatedSupplyRequests()
      setMySupplyRequests(myRequests.slice(0, 5))
    } catch (e: any) {
      alert(e?.message || 'Failed to archive request')
    } finally {
      setArchivingRequestId(null)
    }
  }

  const stats = [
    {
      title: 'Active Deliveries',
      value: mySupplyRequests.length.toString(),
      change: 'Supply requests',
      trend: 'up',
      icon: Truck,
      color: 'text-secondary-600'
    }
  ]

  const urgencyColors = {
    LOW: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800'
  }

  const statusColors = {
    NEW: 'bg-blue-100 text-blue-800',
    ACCEPTED: 'bg-purple-100 text-purple-800',
    PICKED_UP: 'bg-yellow-100 text-yellow-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    EXPIRED: 'bg-red-100 text-red-800'
  }

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const formatListingExpiry = (request: SupplyRequest): string | null => {
    if (!request.expiresAt) return null
    const exp = new Date(request.expiresAt)
    if (Number.isNaN(exp.getTime())) return null
    if (request.status === 'EXPIRED' || exp.getTime() <= Date.now()) {
      return `Expired ${exp.toLocaleString()}`
    }
    return `Listing expires ${exp.toLocaleString()}`
  }

  const canRequestSupplies =
    !!user?.isVerified &&
    !!user?.emailVerified &&
    !!user?.onboardingCompleted

  const needsAdminApproval =
    !!user?.emailVerified &&
    !!user?.onboardingCompleted &&
    !user?.isVerified

  const openSupplyRequestForm = () => {
    if (!canRequestSupplies) {
      if (needsAdminApproval) {
        alert(
          'You cannot create supply requests until an admin approves your food bank account. Complete email verification, onboarding, and business verification, then wait for admin approval.'
        )
      } else {
        alert(
          'You cannot create supply requests yet. Verify your email, complete onboarding and business verification, then wait for an admin to approve your account.'
        )
      }
      return
    }
    setShowPickupForm(true)
  }

  const renderSupplyRequestActions = (request: SupplyRequest) => (
    <div className="flex items-center justify-between gap-3 mt-3 text-sm text-gray-500 flex-wrap">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="flex items-center">
          <Package className="h-4 w-4 mr-1" />
          {request.estimatedWeight}
        </span>
        <span className="flex items-center">
          <MapPin className="h-4 w-4 mr-1" />
          {request.pickupStreet}, {request.pickupCity}
        </span>
        {formatListingExpiry(request) && (
          <span className="flex items-center text-amber-700">
            <Clock className="h-4 w-4 mr-1" />
            {formatListingExpiry(request)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400">
          Created {new Date(request.createdAt).toLocaleDateString()}
        </span>
        {request.archivedAt == null && (
          <button
            type="button"
            onClick={() => handleArchiveRequest(request.id)}
            disabled={archivingRequestId === request.id}
            className={`btn-outline text-xs ${archivingRequestId === request.id ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {archivingRequestId === request.id ? 'Taking down...' : 'Take down'}
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Food Decorations */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <Apple className="absolute top-20 left-12 h-8 w-8 text-red-500 animate-pulse" />
        <Coffee className="absolute top-40 right-16 h-6 w-6 text-amber-600 animate-bounce" />
        <Cake className="absolute bottom-40 left-20 h-10 w-10 text-orange-400 animate-pulse" />
        <Gift className="absolute bottom-20 right-32 h-6 w-6 text-green-500 animate-bounce" />
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 border-b border-gray-200 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="relative mr-3">
                <Package className="h-8 w-8 text-primary-600" />
                <Apple className="absolute -top-1 -right-1 h-3 w-3 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  Welcome, {user?.firstName || 'Food Bank Manager'}!
                  <Gift className="ml-2 h-5 w-5 text-green-500" />
                </h1>
              <p className="text-gray-600 mt-1">
                Here's what's happening with your food bank today
              </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openSupplyRequestForm}
                disabled={!canRequestSupplies}
                title={
                  canRequestSupplies
                    ? 'Request supplies'
                    : 'Available after admin approval'
                }
                className={`btn-primary inline-flex items-center ${!canRequestSupplies ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Truck className="h-4 w-4 mr-2" />
                Request Supplies
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user?.userType === 'FOOD_BANK' && !canRequestSupplies && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm font-semibold text-amber-950">
              {needsAdminApproval
                ? 'Waiting for admin approval — you cannot create supply requests yet'
                : 'Account setup required before you can create supply requests'}
            </p>
            <p className="text-sm text-amber-900 mt-2">
              Food banks <strong>cannot make supply requests until an admin approves your account</strong>.
              Finish the steps below, then wait for approval. You will be able to request supplies only after
              your account shows as approved.
            </p>
            <ul className="mt-3 text-xs text-amber-900 list-disc list-inside space-y-1">
              {!user?.emailVerified && <li>Verify your email (required)</li>}
              {!user?.onboardingCompleted && <li>Complete onboarding (required)</li>}
              {businessVerification?.status !== 'PENDING' &&
                businessVerification?.status !== 'APPROVED' && (
                <li>Submit business verification (required)</li>
              )}
              {!user?.isVerified && (
                <li>Admin approval (required — requests stay locked until then)</li>
              )}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              {!user?.emailVerified && (
                <Link to="/verify-email" className="btn-outline text-sm">
                  Verify email
                </Link>
              )}
              {businessVerification?.status !== 'PENDING' &&
                businessVerification?.status !== 'APPROVED' && (
                <Link to="/business-verification" className="btn-outline text-sm">
                  Business verification
                </Link>
              )}
              {!user?.onboardingCompleted && (
                <Link to="/food-bank-onboarding" className="btn-outline text-sm">
                  Complete onboarding
                </Link>
              )}
              <Link to="/profile" className="btn-outline text-sm">
                Add directory photo
              </Link>
            </div>
            <p className="text-xs text-amber-800 mt-3">
              Business verification status: {businessVerification?.status || 'not submitted'}
              {needsAdminApproval ? ' • Waiting for an admin to approve your account' : ''}
            </p>
          </div>
        )}

        {/* Hero Section with Image */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-6 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Food bank operations" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Serving Our Community</h2>
              <p className="text-gray-600 mb-4">
                Your food bank is a vital part of our network, connecting donors with those in need. 
                Thank you for your dedication to fighting hunger!
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center text-sm text-green-600">
                  <Package className="h-4 w-4 mr-1" />
                  <span>23 pending donations</span>
                </div>
                <div className="flex items-center text-sm text-blue-600">
                  <Users className="h-4 w-4 mr-1" />
                  <span>15 volunteers helping</span>
                </div>
                <div className="flex items-center text-sm text-purple-600">
                  <Truck className="h-4 w-4 mr-1" />
                  <span>Active deliveries</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                alt="Food bank" 
                className="w-48 h-32 object-cover rounded-lg shadow-lg"
              />
              <div className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg">
                <Gift className="h-5 w-5 text-primary-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`p-3 rounded-full bg-gray-50 ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Supply Requests */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    My Supply Requests
                  </h2>
                  <button
                    onClick={openSupplyRequestForm}
                    disabled={!canRequestSupplies}
                    className={`text-sm font-medium ${
                      canRequestSupplies
                        ? 'text-primary-600 hover:text-primary-700'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Create request
                  </button>
                </div>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading requests...</p>
                  </div>
                ) : mySupplyRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No supply requests yet</p>
                    <p className="text-sm text-gray-400 mt-1">Create one to let suppliers know what you need.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {mySupplyRequests.map((req) => (
                      <div key={req.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium text-gray-900">{req.title}</h3>
                          <div className="flex gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[req.urgency as keyof typeof urgencyColors]}`}>
                              {req.urgency}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status as keyof typeof statusColors]}`}>
                              {req.status}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm">{req.description}</p>
                        {renderSupplyRequestActions(req)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Incoming Donations (assigned to this food bank) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Incoming Donations</h2>
                  <Link to="/profile" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                    View all
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading incoming donations...</p>
                  </div>
                ) : incomingDonations.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No incoming donations yet</p>
                    <p className="text-sm text-gray-400 mt-2">When volunteers claim deliveries to your food bank, they’ll show up here.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {incomingDonations.map((donation) => (
                      <div key={donation.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-medium text-gray-900">{donation.title}</h3>
                          <div className="flex gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[donation.urgency as keyof typeof urgencyColors]}`}>
                              {donation.urgency}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[donation.status as keyof typeof statusColors]}`}>
                              {formatStatus(donation.status)}
                            </span>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          {donation.supplier && (
                            <div>
                              <span className="font-medium text-gray-700">Supplier:</span>{' '}
                              {donation.supplier.firstName} {donation.supplier.lastName}
                            </div>
                          )}
                          {donation.volunteer && (
                            <div>
                              <span className="font-medium text-gray-700">Volunteer:</span>{' '}
                              <Link
                                to={`/volunteers/${donation.volunteer.id}`}
                                className="font-semibold text-primary-700 hover:underline"
                              >
                                {donation.volunteer.firstName} {donation.volunteer.lastName}
                              </Link>
                            </div>
                          )}
                        </div>

                        {donation.status === 'ACCEPTED' && (
                          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-900">
                            Waiting for supplier to confirm pickup.
                          </div>
                        )}

                        {donation.status === 'PICKED_UP' && (
                          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="text-sm text-yellow-900">Pickup confirmed — please confirm delivery when received.</div>
                            <button
                              onClick={() => handleConfirmDelivery(donation.id)}
                              disabled={actionDonationId === donation.id}
                              className={`btn-primary ${actionDonationId === donation.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              {actionDonationId === donation.id ? 'Confirming...' : 'Confirm Delivery'}
                            </button>
                          </div>
                        )}

                        {donation.status === 'DELIVERED' && (
                          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
                            🎉 Completed — pickup and delivery confirmed.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions & Inventory */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={openSupplyRequestForm}
                  disabled={!canRequestSupplies}
                  className={`flex items-center p-3 rounded-lg border transition-colors w-full text-left ${
                    canRequestSupplies
                      ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <Truck className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="font-medium text-gray-900">
                    Request Supplies
                    {!canRequestSupplies && (
                      <span className="block text-xs font-normal text-gray-500">
                        Locked until admin approval
                      </span>
                    )}
                  </span>
                </button>
                <Link
                  to="/volunteers"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-accent-300 hover:bg-accent-50 transition-colors"
                >
                  <Users className="h-5 w-5 text-accent-600 mr-3" />
                  <span className="font-medium text-gray-900">View Volunteers</span>
                </Link>
                <Link
                  to="/schedule"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <Calendar className="h-5 w-5 text-gray-600 mr-3" />
                  <span className="font-medium text-gray-900">View Schedule</span>
                </Link>
              </div>
            </div>


            {/* Removed hardcoded recent activity */}
          </div>
        </div>
      </div>

      {/* My Supply Requests Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                My Supply Requests
              </h2>
              <div className="flex items-center gap-3">
                <Link
                  to="/food-bank/requests/archived"
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Archived
                </Link>
                <button
                  onClick={openSupplyRequestForm}
                  disabled={!canRequestSupplies}
                  className={`text-sm font-medium ${
                    canRequestSupplies
                      ? 'text-primary-600 hover:text-primary-700'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Create New Request
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading supply requests...</p>
              </div>
            ) : mySupplyRequests.length === 0 ? (
              <div className="text-center py-8">
                <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No supply requests created yet</p>
                <p className="text-sm text-gray-400 mt-2">Create your first supply request to let volunteers know what you need</p>
              </div>
            ) : (
              <div className="space-y-4">
                {mySupplyRequests.map((request) => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-medium text-gray-900">{request.title}</h3>
                      <div className="flex gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[request.urgency as keyof typeof urgencyColors]}`}>
                          {request.urgency}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[request.status as keyof typeof statusColors]}`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{request.description}</p>
                    {renderSupplyRequestActions(request)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Removed hardcoded "events/resources/success stories" sections */}

      {/* Pickup Request Form Modal */}
      {showPickupForm && canRequestSupplies && (
        <PickupRequestForm
          onClose={() => setShowPickupForm(false)}
          onSubmit={handlePickupRequest}
          isSubmitting={isSubmittingPickup}
          userType="FOOD_BANK"
        />
      )}
    </div>
  )
}

export default FoodBankDashboard